/**
 * Base Dexie DÉDIÉE au module gestion-eau (isolée de BazarKELYDB).
 * Choix d'isolation : aucune migration sur la base partagée → modification
 * strictement additive. Offline-first : toute lecture/écriture passe par Dexie ;
 * la synchronisation Supabase se fait en arrière-plan (cf. services/eauSync.ts).
 *
 * Les noms de colonnes (snake_case) sont IDENTIQUES au schéma Supabase pour rendre
 * la sync triviale. Le champ Dexie-only `_dirty` (non indexé, non transmis) marque
 * les enregistrements en attente de push.
 */
import Dexie, { type Table } from 'dexie';
import type {
  CompteurLocal,
  QrCompteurLocal,
  ReleveCompteurLocal,
  ReleveBassinLocal,
  EntreeBassinLocal,
  BilanLocal,
  DebitTestLocal,
  ArretPompeLocal,
  ElecReleveLocal,
  ElecCoutLocal,
  FactureLocal,
  ConfigLocal,
  RoleLocal,
  CompteClientLocal,
  DemandeAccesLocal,
  InvitationLocal,
  ScanLocal,
  AlerteLocal,
  AuditLocal,
  AnnonceLocal,
  ElecReglagesLocal,
  ElecClientLocal,
  ElecCompteurLocal,
  ElecFactureLocal,
  ElecFactureLigneLocal,
  ElecValidationLocal,
} from '../types/gestionEau';

/**
 * Tombstone de suppression (local-only, JAMAIS synchronisé comme une table de données).
 * Trace une ligne supprimée localement dont le DELETE serveur n'est pas encore confirmé,
 * pour (a) rejouer la suppression au retour en ligne et (b) empêcher le pull de la
 * « ressusciter » dans Dexie. PK déterministe `${table}:${pk_value}` → idempotent.
 */
export interface EauDeletion {
  id: string;
  table: string; // EauTableName
  pk_value: string;
  deleted_at: string;
}

export class GestionEauDB extends Dexie {
  eau_compteurs!: Table<CompteurLocal, string>;
  eau_qr_compteur!: Table<QrCompteurLocal, string>;
  eau_releves_compteur!: Table<ReleveCompteurLocal, string>;
  eau_releves_bassin!: Table<ReleveBassinLocal, string>;
  eau_entrees_bassin!: Table<EntreeBassinLocal, string>;
  eau_bilans!: Table<BilanLocal, string>;
  eau_debit_tests!: Table<DebitTestLocal, string>;
  eau_arrets_pompe!: Table<ArretPompeLocal, string>;
  eau_elec_releves_compteur!: Table<ElecReleveLocal, string>;
  eau_elec_couts!: Table<ElecCoutLocal, string>;
  eau_factures!: Table<FactureLocal, string>;
  eau_config!: Table<ConfigLocal, string>;
  eau_roles!: Table<RoleLocal, string>;
  eau_comptes_client!: Table<CompteClientLocal, string>;
  eau_demandes_acces!: Table<DemandeAccesLocal, string>;
  eau_invitations!: Table<InvitationLocal, string>;
  eau_scans!: Table<ScanLocal, string>;
  eau_alertes!: Table<AlerteLocal, string>;
  eau_audit!: Table<AuditLocal, string>;
  eau_annonces!: Table<AnnonceLocal, string>;
  eau_elec_reglages!: Table<ElecReglagesLocal, string>;
  eau_elec_clients!: Table<ElecClientLocal, string>;
  eau_elec_compteurs!: Table<ElecCompteurLocal, string>;
  eau_elec_factures!: Table<ElecFactureLocal, string>;
  eau_elec_facture_lignes!: Table<ElecFactureLigneLocal, string>;
  eau_elec_validations!: Table<ElecValidationLocal, string>;
  /** Tombstones de suppression (local-only, hors EAU_TABLES). */
  eau_deletions!: Table<EauDeletion, string>;

  constructor() {
    super('GestionEauDB');

    this.version(1).stores({
      // PK = id (text/uuid client). _dirty volontairement NON indexé (filtré en mémoire).
      eau_compteurs: 'id, type, zone, actif, ordre',
      eau_qr_compteur: 'id, compteur_id, code',
      eau_releves_compteur: 'id, compteur_id, timestamp, [compteur_id+timestamp]',
      eau_releves_bassin: 'id, timestamp',
      eau_entrees_bassin: 'id, timestamp',
      eau_bilans: 'id, timestamp, anomalie, traitee',
      eau_factures: 'id, compteur_id, statut',
      eau_config: 'id',
      eau_roles: 'user_id, admin, releveur', // PK = user_id
      eau_comptes_client: 'id, user_id, code_enrolement, code_qr',
      eau_demandes_acces: 'id, user_id, statut',
      eau_scans: 'id, compteur_id, timestamp, [compteur_id+timestamp]',
      eau_alertes: 'id, type, lu, traitee',
      eau_audit: 'id, timestamp',
      eau_annonces: 'id, actif',
    });

    // v2 — Évolution « bassin/débit » : nouvelle table des tests de débit (vanne fermée).
    // Additif : Dexie reporte automatiquement les stores inchangés.
    this.version(2).stores({
      eau_debit_tests: 'id, timestamp',
    });

    // v3 — Invitations par email (octroi automatique du rôle au 1er login Google).
    // Additif : Dexie reporte automatiquement les stores inchangés (aucune perte).
    this.version(3).stores({
      eau_invitations: 'id, email, statut',
    });

    // v4 — Invitation vitrine WhatsApp par JETON : index `token` (match au login
    // sur le jeton, pas l'email). Additif : Dexie reporte les stores inchangés.
    this.version(4).stores({
      eau_invitations: 'id, email, statut, token',
    });

    // v5 — Fiche d'accès enrichie (ÉVO 1) : `eau_demandes_acces` gagne phone/fonction/
    // message (champs texte simples, NON indexés). Le schéma d'index reste inchangé →
    // simple bump de version pour signaler la nouvelle forme ; Dexie conserve les données.
    this.version(5).stores({
      eau_demandes_acces: 'id, user_id, statut',
    });

    // v6 — Socle ÉLECTRICITÉ (facture combinée eau+élec, Phase 1) : relevés de compteur
    // électrique (kWh) + coûts mensuels de la centrale. Additif : Dexie reporte
    // automatiquement les stores inchangés (aucune perte sur les tables existantes).
    this.version(6).stores({
      eau_elec_releves_compteur: 'id, compteur_id, timestamp, [compteur_id+timestamp]',
      eau_elec_couts: 'id, mois',
    });

    // v7 — Arrêts de pompe (saisie des périodes pompe à l'arrêt) : sert au temps de
    // marche réel pour l'apport « débit × temps de marche » (modèle réseau, Phase 2).
    // Additif : Dexie reporte automatiquement les stores inchangés (aucune perte).
    this.version(7).stores({
      eau_arrets_pompe: 'id, timestamp_debut',
    });

    // v8 — Tombstones de suppression (offline-first) : trace les lignes supprimées
    // localement dont le DELETE serveur n'est pas encore confirmé, pour rejeu idempotent
    // au retour en ligne et anti-résurrection au pull. Store LOCAL-ONLY (hors EAU_TABLES,
    // jamais poussé/tiré vers Supabase). Additif : Dexie reporte les stores inchangés.
    this.version(8).stores({
      eau_deletions: 'id, table',
    });

    // v9 — Socle RTB Énergie (ELEC-CC-02) : réglages, clients et compteurs d'électricité.
    // `eau_elec_releves_compteur` garde son schéma d'index. Additif : aucune perte.
    this.version(9).stores({
      eau_elec_reglages: 'id',
      eau_elec_clients: 'id, code, statut',
      eau_elec_compteurs: 'id, code, client_id, statut',
    });

    // v10 : Factures RTB Énergie (ELEC-CC-03) : factures, lignes, avis des valideurs.
    // Additif : Dexie reporte automatiquement les stores inchangés (aucune perte).
    this.version(10).stores({
      eau_elec_factures: 'id, cycle, client_id, statut, numero',
      eau_elec_facture_lignes: 'id, facture_id, compteur_id',
      eau_elec_validations: 'id, facture_id, user_id',
    });
  }
}

export const eauDb = new GestionEauDB();

/** Liste des tables synchronisables (nom Supabase == nom Dexie). */
export const EAU_TABLES = [
  'eau_compteurs',
  'eau_qr_compteur',
  'eau_releves_compteur',
  'eau_releves_bassin',
  'eau_entrees_bassin',
  'eau_bilans',
  'eau_debit_tests',
  'eau_arrets_pompe',
  'eau_elec_couts',
  'eau_factures',
  'eau_config',
  'eau_roles',
  'eau_comptes_client',
  'eau_demandes_acces',
  'eau_invitations',
  'eau_scans',
  'eau_alertes',
  'eau_audit',
  'eau_annonces',
  // Socle RTB Énergie (ELEC-CC-02). Les clients avant les compteurs, les compteurs avant
  // les relevés : l'ordre de push respecte les clés étrangères.
  'eau_elec_reglages',
  'eau_elec_clients',
  'eau_elec_compteurs',
  'eau_elec_releves_compteur',
  // Factures RTB (ELEC-CC-03) : la facture avant ses lignes ; les avis sont tirés seulement.
  'eau_elec_factures',
  'eau_elec_facture_lignes',
  'eau_elec_validations',
] as const;

export type EauTableName = (typeof EAU_TABLES)[number];
